import React, { type JSX, useMemo } from "react";

import { TERMINOLOGY } from "../../hooks/useTerminologyText";
import { ClientCounterEvent } from "../../util/clientCounterEvent";
import { TestSelectors } from "../../util/testSelectors";
import {
  DataSourcesIcon,
  EnvironmentIcon,
  FilesIcon,
  HelpIcon,
  HistoryIcon,
  OutlineIcon,
  ProjectSearchIcon,
  ScheduledRunsSidebarIcon,
  VariablesIcon,
} from "../icons/CustomIcons";

export const DEFAULT_SIDEBAR_WIDTH = 341;
export const DEFAULT_AGENT_CHAT_SIDEBAR_WIDTH = 400;
export const THREAD_PANE_MIN_WIDTH_PX = 250;
export const COLLAPSED_SIDEBAR_WIDTH = 48;
export const MAX_SIDEBAR_WIDTH = 600;
export const MAX_AGENT_CHAT_SIDEBAR_WIDTH = 1200;
export enum SidebarTab {
  DATA_CONNECTIONS = "data-connections",
  ENVIRONMENT = "environment",
  FILES = "files",
  HISTORY = "history",
  LEARN = "learn",
  SCHEDULED_RUNS = "scheduled-runs",
  SEARCH = "search",
  TOC = "toc",
  VARIABLES = "variables",
}

export interface SidebarTabData {
  icon: JSX.Element;
  slug: SidebarTab;
  title: string;
  counterEvent?: ClientCounterEvent;
  dataTest?: string;
  // optionally set if the title is different in the component view
  componentsTitle?: string;
  location?: "top" | "bottom-upper" | "bottom-lower";
}

export const useSidebarTabInfo: () => Record<
  SidebarTab,
  SidebarTabData
> = () => {
  return useMemo(
    () => ({
      [SidebarTab.TOC]: {
        icon: <OutlineIcon />,
        slug: SidebarTab.TOC,
        title: "Table of contents",
        dataTest: TestSelectors.OUTLINE_TAB,
        counterEvent: "sidebar.click_tab.toc",
      },
      [SidebarTab.SEARCH]: {
        icon: <ProjectSearchIcon />,
        slug: SidebarTab.SEARCH,
        title: "Find",
        counterEvent: "sidebar.click_tab.search",
      },
      [SidebarTab.DATA_CONNECTIONS]: {
        icon: <DataSourcesIcon />,
        slug: SidebarTab.DATA_CONNECTIONS,
        dataTest: TestSelectors.DATA_CONNECTION_TAB,
        title: TERMINOLOGY.dataBrowserText,
        counterEvent: "sidebar.click_tab.data_browser",
      },
      [SidebarTab.ENVIRONMENT]: {
        icon: <EnvironmentIcon />,
        slug: SidebarTab.ENVIRONMENT,
        title: "Environment",
        counterEvent: "sidebar.click_tab.environment",
      },
      [SidebarTab.FILES]: {
        icon: <FilesIcon />,
        slug: SidebarTab.FILES,
        title: "Files",
        counterEvent: "sidebar.click_tab.files",
      },
      [SidebarTab.VARIABLES]: {
        icon: <VariablesIcon />,
        slug: SidebarTab.VARIABLES,
        dataTest: TestSelectors.VARIABLES_TAB,
        title: "Variables",
        counterEvent: "sidebar.click_tab.variables",
      },
      [SidebarTab.SCHEDULED_RUNS]: {
        icon: <ScheduledRunsSidebarIcon />,
        slug: SidebarTab.SCHEDULED_RUNS,
        title: "Scheduled runs",
        dataTest: TestSelectors.SCHEDULED_RUNS_SIDEBAR_TAB,
        counterEvent: "sidebar.click_tab.scheduled_runs",
      },
      [SidebarTab.HISTORY]: {
        icon: <HistoryIcon />,
        slug: SidebarTab.HISTORY,
        dataTest: TestSelectors.VERSION_TAB,
        title: "History & versions",
        counterEvent: "sidebar.click_tab.history_versions",
      },
      [SidebarTab.LEARN]: {
        icon: <HelpIcon />,
        slug: SidebarTab.LEARN,
        title: "Help & support",
        location: "bottom-lower",
        dataTest: TestSelectors.HELP_AND_SUPPORT,
        counterEvent: "sidebar.click_tab.help",
      },
    }),
    [],
  );
};
